<template>
  <div
    class="properties-panel"
    :class="{ collapsed, 'properties-panel--idle': isIdleSelection }"
    @click.stop
  >
    <!-- 标题栏 -->
    <div class="pp-header">
      <span class="pp-title">
        <template v-if="selectedPathId && !objectId">
          {{ currentPath?.name ?? '路径' }}
        </template>
        <template v-else-if="objectId">
          <span v-if="!isMultiSelect" class="pp-title-inner">
            <span class="pp-title-main">{{ objDisplayTitle }}</span>
            <span v-if="componentTypeSubtitle" class="pp-type-sub">{{ componentTypeSubtitle }}</span>
          </span>
          <span v-else>已选中 {{ objectIds!.length }} 个对象</span>
        </template>
        <span v-else class="pp-placeholder">选择一个对象或路径</span>
      </span>
      <button class="pp-collapse-btn" @click="collapsed = !collapsed" :title="collapsed ? '展开' : '折叠'">
        {{ collapsed ? '▼' : '▲' }}
      </button>
    </div>

    <template v-if="selectedPathId && !objectId && !collapsed">
      <div class="pp-scroll">
        <div class="pp-section-label">路径属性</div>
        <div class="pp-appearance-row">
          <label>名称</label>
          <input v-model="pathNameDraft" type="text" class="pp-text-input" @change="commitPathName" />
        </div>
        <div class="pp-appearance-row">
          <label>颜色</label>
          <input type="color" :value="currentPath?.color ?? '#3b82f6'" @input="onPathColorInput($event)" />
        </div>
        <div class="pp-follow-floor-row">
          <label class="pp-follow-floor-label">
            <input type="checkbox" :checked="currentPath?.closed" @change="onPathToggle('closed', ($event.target as HTMLInputElement).checked)" />
            闭合环路
          </label>
        </div>
        <div class="pp-follow-floor-row">
          <label class="pp-follow-floor-label">
            <input type="checkbox" :checked="currentPath?.smooth" @change="onPathToggle('smooth', ($event.target as HTMLInputElement).checked)" />
            样条平滑
          </label>
        </div>
        <div class="pp-follow-floor-row">
          <label class="pp-follow-floor-label">
            <input type="checkbox" :checked="currentPath?.visible !== false" @change="onPathToggle('visible', ($event.target as HTMLInputElement).checked)" />
            显示辅助线
          </label>
        </div>
        <div class="pp-appearance-row">
          <label>限速(m/s)</label>
          <input
            type="number"
            step="0.1"
            min="0"
            class="coord-input"
            :value="pathSpeedLimitDisplay"
            placeholder="不限"
            @change="onPathSpeedLimitChange(($event.target as HTMLInputElement).value)"
          />
        </div>
        <div v-if="pathRefCount > 0" class="pp-section-hint">共 {{ pathRefCount }} 辆车使用此路径</div>
        <div class="pp-row">
          <button type="button" class="pp-btn pp-btn-danger" @click="deletePathFromPanel">删除路径</button>
        </div>
      </div>
    </template>

    <template v-else-if="objectId && !collapsed">
      <div class="pp-scroll">
        <template v-if="isMultiSelect">
          <div class="pp-selection-list">
            <p class="pp-section-hint">当前选中 {{ objectIds!.length }} 个对象，点名称可单独编辑。</p>
            <button
              v-for="sid in (objectIds ?? [])"
              :key="sid"
              type="button"
              class="pp-name-row"
              :class="{ active: sid === primaryDetailId }"
              @click="pickSelectedObject(sid)"
            >
              <span class="pp-name-row-title">{{ chipDisplayName(sid) }}</span>
              <span v-if="selectedTypeName(sid)" class="pp-name-row-type">{{ selectedTypeName(sid) }}</span>
            </button>
          </div>
        </template>

        <div v-else class="pp-editor">
          <div class="pp-tabs">
          <button
            class="pp-tab"
            :class="{ active: activeTab === 'transform' }"
            @click="activeTab = 'transform'"
          >变换</button>
          <button
            v-if="showAppearanceTab"
            class="pp-tab"
            :class="{ active: activeTab === 'appearance' }"
            @click="activeTab = 'appearance'"
          >外观</button>
          <button
            v-if="behaviorParams.length > 0 && !mergeBehavior"
            class="pp-tab"
            :class="{ active: activeTab === 'behavior' }"
            @click="activeTab = 'behavior'"
          >参数</button>
          <button
            class="pp-tab"
            :class="{ active: activeTab === 'data' }"
            @click="activeTab = 'data'"
          >数据</button>
        </div>

        <div class="pp-body">
          <!-- 变换 Tab -->
          <div v-show="activeTab === 'transform'">
            <!-- 位置输入 -->
            <div v-if="positionDisplay" class="pp-coord-row">
              <span class="coord-label">X</span>
              <input
                class="coord-input"
                type="number"
                step="0.5"
                :value="coordX"
                @change="onCoordChange('x', ($event.target as HTMLInputElement).value)"
              />
              <span class="coord-label">Z</span>
              <input
                class="coord-input"
                type="number"
                step="0.5"
                :value="coordZ"
                @change="onCoordChange('z', ($event.target as HTMLInputElement).value)"
              />
              <span class="coord-label">Y</span>
              <input
                class="coord-input"
                type="number"
                step="0.1"
                :value="coordY"
                @change="onCoordChange('y', ($event.target as HTMLInputElement).value)"
              />
            </div>
            <div v-if="positionDisplay" class="pp-follow-floor-row">
              <button type="button" class="pp-btn" @click="snapToFloor" title="把物体底部放到当前楼面">
                贴回楼面
              </button>
            </div>
            <div v-if="effectiveObjectId" class="pp-row pp-lock-row">
              <button
                type="button"
                class="pp-btn"
                :class="{ active: detailLocked }"
                :title="detailLocked ? '解锁后可拖动、改尺寸' : '锁定后禁止拖动与缩放'"
                @click="toggleDetailLock"
              >
                <Lock v-if="detailLocked" :size="14" class="pp-lock-icon" />
                <Unlock v-else :size="14" class="pp-lock-icon" />
                {{ detailLocked ? '解锁编辑' : '锁定编辑' }}
              </button>
            </div>
            <!-- 几何参数 + 旋转 -->
            <SizePanel :id="effectiveObjectId!" geometry-only :on-history-push="props.onHistoryPush" />
            <div v-if="isVehicleObject" class="pp-path-section">
              <div class="pp-section-label">路径跟随</div>
              <div class="pp-appearance-row">
                <label>路径</label>
                <select class="pp-select" :value="vehiclePathId" @change="onVehiclePathChange(($event.target as HTMLSelectElement).value)">
                  <option value="">未分配</option>
                  <option v-for="p in allPaths" :key="p.id" :value="p.id">{{ p.name }}</option>
                </select>
              </div>
              <div class="pp-appearance-row">
                <label>起始偏移</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="1"
                  class="coord-input"
                  :value="vehiclePathOffset"
                  @change="onVehiclePathOffsetChange(($event.target as HTMLInputElement).value)"
                />
                <span class="coord-y-hint">0~1</span>
              </div>
              <div class="pp-appearance-row">
                <label>方向</label>
                <select class="pp-select" :value="vehicleDirection" @change="onVehicleDirectionChange(($event.target as HTMLSelectElement).value)">
                  <option value="forward">正向</option>
                  <option value="backward">反向</option>
                </select>
              </div>
              <div class="pp-follow-floor-row">
                <label class="pp-follow-floor-label">
                  <input type="checkbox" :checked="vehicleLoop" @change="onVehicleLoopChange(($event.target as HTMLInputElement).checked)" />
                  循环行驶
                </label>
              </div>
              <div v-if="vehiclePathUsageCount > 0" class="pp-section-hint">当前路径共 {{ vehiclePathUsageCount }} 辆车使用</div>
            </div>
          </div>

          <!-- 外观 Tab -->
          <div v-show="activeTab === 'appearance'">
            <template v-if="isImportedModel">
              <div class="pp-mini-tabs">
                <button
                  class="pp-mini-tab"
                  :class="{ active: appearanceTab === 'material' }"
                  @click="appearanceTab = 'material'"
                >材质</button>
                <button
                  class="pp-mini-tab"
                  :class="{ active: appearanceTab === 'parts' }"
                  @click="appearanceTab = 'parts'"
                >部件</button>
              </div>
              <div v-if="appearanceTab === 'material'">
                <MaterialEditPanel :object-id="effectiveObjectId!" :on-history-push="props.onHistoryPush" @changed="() => {}" />
              </div>
              <div v-else>
                <ModelPartsPanel :object-id="effectiveObjectId!" :on-history-push="props.onHistoryPush" />
              </div>
            </template>
            <template v-else>
              <div class="pp-appearance-grid">
                <!-- 颜色 params -->
                <div v-for="p in colorParams" :key="p.key" class="pp-param-cell pp-param-cell--color">
                  <label class="pp-param-cell-label">{{ p.label }}</label>
                  <div class="pp-param-cell-control">
                    <input
                      type="color"
                      :value="obj?.params?.[p.key] ?? p.default"
                      @input="onParamColorChange(p.key, ($event.target as HTMLInputElement).value)"
                    />
                    <span class="pp-color-hex">{{ String(obj?.params?.[p.key] ?? p.default ?? '') }}</span>
                  </div>
                </div>
                <!-- 如果没有 color param，用通用颜色 -->
                <div
                  v-if="colorParams.length === 0 && appearanceNonColorParams.length === 0 && !(mergeBehavior && behaviorParams.length > 0)"
                  class="pp-param-cell pp-param-cell--color"
                >
                  <label class="pp-param-cell-label">颜色</label>
                  <div class="pp-param-cell-control">
                    <input type="color" :value="currentColor" @input="onColorChange($event)" />
                    <span class="pp-color-hex">{{ currentColor }}</span>
                  </div>
                </div>
                <!-- appearance 区的非颜色参数（toggle / select / number） -->
                <template v-for="p in appearanceNonColorParams" :key="p.key">
                  <div class="pp-param-cell" :class="{ 'pp-param-cell--wide': p.key === 'flowMode' }">
                    <label v-if="p.type !== 'toggle'" class="pp-param-cell-label">{{ p.label }}</label>
                    <div class="pp-param-cell-control" :class="{ 'pp-param-cell-control--toggle': p.type === 'toggle' }">
                      <template v-if="p.type === 'toggle'">
                        <span class="pp-toggle-text">{{ p.label }}</span>
                        <input
                          type="checkbox"
                          :checked="Boolean(obj?.params?.[p.key] ?? p.default)"
                          @change="onParamChange(p.key, ($event.target as HTMLInputElement).checked)"
                        />
                      </template>
                      <template v-else-if="p.type === 'select' && p.key === 'flowMode'">
                        <div class="pp-material-btns">
                          <button
                            v-for="opt in p.options"
                            :key="String(opt.value)"
                            class="pp-mat-btn"
                            :class="{ active: String(obj?.params?.[p.key] ?? p.default ?? '') === String(opt.value) }"
                            @click="onParamChange(p.key, opt.value)"
                          >
                            {{ opt.label }}
                          </button>
                        </div>
                      </template>
                      <!-- select 且无 options：约定为自由文本参数（如标签文字），见 SizePanel.vue 同款回退 -->
                      <template v-else-if="p.type === 'select' && (!p.options || p.options.length === 0)">
                        <div v-if="isUrlLikeParam(p)" class="pp-url-row">
                          <input
                            type="text"
                            class="pp-text-input"
                            :value="String(obj?.params?.[p.key] ?? p.default ?? '')"
                            placeholder="图片地址或文件名"
                            @change="onParamChange(p.key, ($event.target as HTMLInputElement).value)"
                          />
                          <button type="button" class="pp-file-btn" @click="openFileForParam(p)">打开文件</button>
                        </div>
                        <input
                          v-else
                          type="text"
                          class="pp-text-input"
                          :value="String(obj?.params?.[p.key] ?? p.default ?? '')"
                          @change="onParamChange(p.key, ($event.target as HTMLInputElement).value)"
                        />
                      </template>
                      <template v-else-if="p.type === 'select'">
                        <select
                          class="pp-select"
                          :value="String(obj?.params?.[p.key] ?? p.default ?? '')"
                          @change="onParamChange(p.key, ($event.target as HTMLSelectElement).value)"
                        >
                          <option v-for="opt in p.options" :key="String(opt.value)" :value="opt.value">{{ opt.label }}</option>
                        </select>
                      </template>
                      <template v-else-if="p.type === 'number'">
                        <input
                          type="number"
                          :min="p.min ?? 0"
                          :max="p.max ?? 100"
                          :step="p.step ?? 1"
                          :value="getNumberParamInputValue(p.key, obj?.params?.[p.key], p.default)"
                          class="coord-input pp-number-input"
                          @input="onNumberParamInput(p.key, ($event.target as HTMLInputElement).value)"
                          @change="commitNumberParamChange(p.key, ($event.target as HTMLInputElement).value, p.default)"
                          @keydown.enter="commitNumberParamChange(p.key, ($event.target as HTMLInputElement).value, p.default)"
                        />
                      </template>
                    </div>
                  </div>
                </template>
                <!-- 合并到外观的 behavior 参数（mergeBehaviorToAppearance） -->
                <template v-if="mergeBehavior" v-for="p in behaviorParams" :key="p.key">
                  <div class="pp-param-cell">
                    <label v-if="p.type !== 'toggle'" class="pp-param-cell-label">{{ p.label }}</label>
                    <div class="pp-param-cell-control" :class="{ 'pp-param-cell-control--toggle': p.type === 'toggle' }">
                      <template v-if="p.type === 'toggle'">
                        <span class="pp-toggle-text">{{ p.label }}</span>
                        <input
                          type="checkbox"
                          :checked="Boolean(obj?.params?.[p.key] ?? p.default)"
                          @change="onParamChange(p.key, ($event.target as HTMLInputElement).checked)"
                        />
                      </template>
                      <template v-else-if="p.type === 'select' && (!p.options || p.options.length === 0)">
                        <div v-if="isUrlLikeParam(p)" class="pp-url-row">
                          <input
                            type="text"
                            class="pp-text-input"
                            :value="String(obj?.params?.[p.key] ?? p.default ?? '')"
                            placeholder="图片地址或文件名"
                            @change="onParamChange(p.key, ($event.target as HTMLInputElement).value)"
                          />
                          <button type="button" class="pp-file-btn" @click="openFileForParam(p)">打开文件</button>
                        </div>
                        <input
                          v-else
                          type="text"
                          class="pp-text-input"
                          :value="String(obj?.params?.[p.key] ?? p.default ?? '')"
                          @change="onParamChange(p.key, ($event.target as HTMLInputElement).value)"
                        />
                      </template>
                      <template v-else-if="p.type === 'select'">
                        <select
                          class="pp-select"
                          :value="String(obj?.params?.[p.key] ?? p.default ?? '')"
                          @change="onParamChange(p.key, ($event.target as HTMLSelectElement).value)"
                        >
                          <option v-for="opt in p.options" :key="String(opt.value)" :value="opt.value">{{ opt.label }}</option>
                        </select>
                      </template>
                      <template v-else-if="p.type === 'number'">
                        <input
                          type="number"
                          :min="p.min ?? 0"
                          :max="p.max ?? 100"
                          :step="p.step ?? 1"
                          :value="getNumberParamInputValue(p.key, obj?.params?.[p.key], p.default)"
                          class="coord-input pp-number-input"
                          @input="onNumberParamInput(p.key, ($event.target as HTMLInputElement).value)"
                          @change="commitNumberParamChange(p.key, ($event.target as HTMLInputElement).value, p.default)"
                          @keydown.enter="commitNumberParamChange(p.key, ($event.target as HTMLInputElement).value, p.default)"
                        />
                      </template>
                    </div>
                  </div>
                </template>
              </div>
              <!-- 材质类型（仅当有 wallMaterial 参数时显示） -->
              <div v-if="hasMaterialParam" class="pp-appearance-row pp-material-row">
                <label>材质</label>
                <div class="pp-material-btns">
                  <button
                    v-for="mt in materialTypes"
                    :key="mt.value"
                    class="pp-mat-btn"
                    :class="{ active: currentMaterial === mt.value }"
                    @click="onMaterialChange(mt.value)"
                  >{{ mt.label }}</button>
                </div>
              </div>
            </template>
          </div>

          <!-- 参数 Tab -->
          <div v-show="activeTab === 'behavior'">
            <div v-for="p in behaviorParams" :key="p.key" class="pp-param-row">
              <label class="pp-param-label">{{ p.label }}</label>
              <!-- select 且无 options：约定为自由文本参数，见 SizePanel.vue 同款回退 -->
              <input
                v-if="p.type === 'select' && (!p.options || p.options.length === 0)"
                type="text"
                class="pp-text-input"
                :value="obj?.params?.[p.key] ?? p.default"
                @change="onParamChange(p.key, ($event.target as HTMLInputElement).value)"
              />
              <!-- select -->
              <select
                v-else-if="p.type === 'select'"
                class="pp-select"
                :value="obj?.params?.[p.key] ?? p.default"
                @change="onParamChange(p.key, ($event.target as HTMLSelectElement).value)"
              >
                <option v-for="opt in p.options" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
              </select>
              <!-- toggle -->
              <input
                v-else-if="p.type === 'toggle'"
                type="checkbox"
                :checked="obj?.params?.[p.key] ?? p.default"
                @change="onParamChange(p.key, ($event.target as HTMLInputElement).checked)"
              />
              <input
                v-else-if="p.type === 'number'"
                type="number"
                class="coord-input"
                :min="p.min ?? 0"
                :max="p.max ?? 100"
                :step="p.step ?? 1"
                :value="getNumberParamInputValue(p.key, obj?.params?.[p.key], p.default)"
                @input="onNumberParamInput(p.key, ($event.target as HTMLInputElement).value)"
                @change="commitNumberParamChange(p.key, ($event.target as HTMLInputElement).value, p.default)"
                @keydown.enter="commitNumberParamChange(p.key, ($event.target as HTMLInputElement).value, p.default)"
              />
            </div>
          </div>

          <!-- 数据 Tab -->
          <div v-show="activeTab === 'data'" class="pp-data-panel">
            <template v-if="connectSummary.paired || connectSummary.cardFields.length">
              <div class="pp-connect-summary">
                <div class="pp-summary-row">
                  <span class="pp-summary-label">已对应</span>
                  <span class="pp-summary-value">{{ connectSummaryLabel }}</span>
                </div>
                <div v-if="connectSummary.cardFields.length" class="pp-summary-row">
                  <span class="pp-summary-label">卡片显示</span>
                  <span class="pp-summary-value">{{ connectSummary.cardFields.join('、') }}</span>
                </div>
                <button type="button" class="pp-confirm-btn pp-connect-change" @click="openBindingDrawer">
                  更改
                </button>
              </div>
            </template>
            <template v-else>
              <p class="pp-section-hint">选一个数据服务、系统设备，勾选要显示的卡片字段；登录在「项目连接」配一次即可。</p>
              <button type="button" class="pp-confirm-btn" @click="openBindingDrawer">绑定设备</button>
            </template>

            <div class="pp-label-divider" />

            <div class="pp-mini-tabs">
              <button class="pp-mini-tab" :class="{ active: dataTab === 'label' }" @click="dataTab = 'label'">标签</button>
              <button
                v-if="effectiveObjectId"
                class="pp-mini-tab"
                :class="{ active: dataTab === 'physicsBindings' }"
                @click="dataTab = 'physicsBindings'"
              >
                仿真绑定
              </button>
            </div>
            <div v-show="dataTab === 'label'">
              <div class="pp-section-hint">物体上方的标签（CSS2D）：名称与状态相互独立，可单独或同时显示</div>

              <div class="pp-label-toggle-row">
                <label class="pp-label-toggle">
                  <input
                    type="checkbox"
                    :checked="topLabelVisible"
                    @change="onTopLabelVisible(($event.target as HTMLInputElement).checked)"
                  />
                  显示名称标签
                </label>
                <label class="pp-label-toggle">
                  <input
                    type="checkbox"
                    :checked="statusLightVisible"
                    @change="onStatusLightVisible(($event.target as HTMLInputElement).checked)"
                  />
                  显示状态标签
                </label>
                <label class="pp-label-toggle">
                  <input
                    type="checkbox"
                    :checked="infoCardVisible"
                    @change="onInfoCardVisible(($event.target as HTMLInputElement).checked)"
                  />
                  显示信息卡
                </label>
              </div>

              <template v-if="infoCardVisible">
                <p v-if="!infoCardFieldKeys.length" class="pp-section-hint">
                  信息卡还没有字段。请在「数据」上方绑定设备并勾选卡片字段，或用「信息卡模板」快速添加。
                </p>
                <div v-else class="pp-appearance-row">
                  <label>卡片字段</label>
                  <span class="pp-summary-value">{{ infoCardFieldKeys.join('、') }}</span>
                </div>
              </template>

              <template v-if="topLabelVisible">
                <div class="pp-appearance-row">
                  <label>标签文字</label>
                  <input
                    type="text"
                    class="pp-text-input"
                    :value="topLabelTextDraft"
                    placeholder="留空则使用对象列表中的名称"
                    @change="onTopLabelTextCommit(($event.target as HTMLInputElement).value)"
                  />
                </div>
                <div class="pp-appearance-row">
                  <label>整体缩放</label>
                  <input
                    type="number"
                    class="coord-input"
                    step="0.05"
                    min="0.35"
                    max="4"
                    :value="resolvedTopLabel.scale"
                    @change="commitLabelPatch({ scale: Number(($event.target as HTMLInputElement).value) })"
                  />
                </div>
                <div class="pp-appearance-row">
                  <label>字号(px)</label>
                  <input
                    type="number"
                    class="coord-input"
                    step="1"
                    min="8"
                    max="32"
                    :value="resolvedTopLabel.fontSizePx"
                    @change="commitLabelPatch({ fontSizePx: Number(($event.target as HTMLInputElement).value) })"
                  />
                </div>
                <div class="pp-appearance-row">
                  <label>垂直偏移(m)</label>
                  <input
                    type="number"
                    class="coord-input"
                    step="0.05"
                    min="-5"
                    max="15"
                    :value="resolvedTopLabel.offsetY"
                    @change="commitLabelPatch({ offsetY: Number(($event.target as HTMLInputElement).value) })"
                  />
                </div>
                <div class="pp-appearance-row">
                  <label>背景</label>
                  <input
                    type="color"
                    :value="resolvedTopLabel.backgroundColor"
                    @input="commitLabelPatch({ backgroundColor: ($event.target as HTMLInputElement).value })"
                  />
                </div>
                <div class="pp-appearance-row">
                  <label>背景透明度</label>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.05"
                    :value="resolvedTopLabel.backgroundOpacity"
                    @input="commitLabelPatch({ backgroundOpacity: Number(($event.target as HTMLInputElement).value) })"
                  />
                </div>
                <div class="pp-appearance-row">
                  <label>文字色</label>
                  <input
                    type="color"
                    :value="resolvedTopLabel.textColor"
                    @input="commitLabelPatch({ textColor: ($event.target as HTMLInputElement).value })"
                  />
                </div>
                <div class="pp-appearance-row">
                  <label>边框色</label>
                  <input
                    type="color"
                    :value="resolvedTopLabel.borderColor"
                    @input="commitLabelPatch({ borderColor: ($event.target as HTMLInputElement).value })"
                  />
                </div>
                <div class="pp-appearance-row">
                  <label>边框宽(px)</label>
                  <input
                    type="number"
                    class="coord-input"
                    step="1"
                    min="0"
                    max="8"
                    :value="resolvedTopLabel.borderWidth"
                    @change="commitLabelPatch({ borderWidth: Number(($event.target as HTMLInputElement).value) })"
                  />
                </div>
                <div class="pp-appearance-row">
                  <label>圆角(px)</label>
                  <input
                    type="number"
                    class="coord-input"
                    step="1"
                    min="0"
                    max="24"
                    :value="resolvedTopLabel.borderRadius"
                    @change="commitLabelPatch({ borderRadius: Number(($event.target as HTMLInputElement).value) })"
                  />
                </div>
                <div class="pp-follow-floor-row">
                  <label class="pp-follow-floor-label">
                    <input
                      type="checkbox"
                      :checked="resolvedTopLabel.showArrow"
                      @change="commitLabelPatch({ showArrow: ($event.target as HTMLInputElement).checked })"
                    />
                    显示指示小三角
                  </label>
                </div>
                <div v-if="resolvedTopLabel.showArrow" class="pp-appearance-row">
                  <label>三角位置</label>
                  <select
                    class="pp-select"
                    :value="resolvedTopLabel.arrowAlign"
                    @change="commitLabelPatch({ arrowAlign: ($event.target as HTMLSelectElement).value as 'left' | 'center' | 'right' })"
                  >
                    <option value="left">左侧</option>
                    <option value="center">居中</option>
                    <option value="right">右侧</option>
                  </select>
                </div>
              </template>

              <template v-if="statusLightVisible">
                <div v-if="topLabelVisible" class="pp-label-divider" />
                <div class="pp-status-row">
                  <button
                    v-for="c in statusColors"
                    :key="c.value"
                    class="pp-status-btn"
                    :class="{ active: statusLightColor === c.value }"
                    @click="statusLightColor = c.value"
                  >
                    <Circle class="status-dot" :size="14" :style="{ color: c.color }" /> {{ c.label }}
                  </button>
                </div>
                <div class="pp-appearance-row">
                  <label>文字</label>
                  <input v-model="statusLightText" placeholder="运行中" class="pp-text-input" />
                </div>
                <div class="pp-panel-actions">
                  <button class="pp-confirm-btn" @click="saveStatusLight">保存状态标签</button>
                </div>
              </template>
            </div>

            <!-- 保存反馈 toast -->
            <transition name="pp-toast-fade">
              <div
                v-if="saveToast"
                class="pp-save-toast"
                :class="saveToast.type === 'error' ? 'pp-save-toast--error' : 'pp-save-toast--ok'"
              >
                {{ saveToast.msg }}
              </div>
            </transition>

            <div v-show="dataTab === 'physicsBindings'" class="pp-physics-bindings">
              <p class="pp-section-hint">对象 metrics → 仿真资源（写入 scene.physicsConfig.bindings）</p>
              <template v-if="effectiveObjectId">
                <div v-for="b in objectPhysicsBindings" :key="b.id" class="pp-physics-row">
                  <span class="pp-physics-label">源指标</span>
                  <input
                    class="pp-card-input"
                    :value="b.sourceMetric"
                    @change="patchPhysicsBinding(b.id, { sourceMetric: ($event.target as HTMLInputElement).value.trim() })"
                  />
                  <span class="pp-physics-label">目标</span>
                  <select
                    class="pp-select"
                    :value="b.targetType"
                    @change="patchPhysicsBinding(b.id, { targetType: ($event.target as HTMLSelectElement).value })"
                  >
                    <option value="zone">zone</option>
                    <option value="flow">flow</option>
                    <option value="source">source</option>
                  </select>
                  <input
                    class="pp-card-input"
                    placeholder="targetId"
                    :value="b.targetId"
                    @change="patchPhysicsBinding(b.id, { targetId: ($event.target as HTMLInputElement).value.trim() })"
                  />
                  <input
                    class="pp-card-input"
                    placeholder="targetMetric"
                    :value="b.targetMetric"
                    @change="patchPhysicsBinding(b.id, { targetMetric: ($event.target as HTMLInputElement).value.trim() })"
                  />
                  <button type="button" class="pp-remove-btn" title="删除" @click="removePhysicsBinding(b.id)">
                    <X :size="14" />
                  </button>
                </div>
                <button type="button" class="pp-add-btn" @click="addPhysicsBinding">＋ 添加绑定</button>
              </template>
            </div>
          </div>
        </div><!-- pp-body -->
        </div><!-- pp-editor -->
      </div><!-- pp-scroll -->
      <input
        ref="urlFileInputRef"
        type="file"
        accept="image/*"
        class="pp-hidden-file"
        @change="onUrlFileChosen($event)"
      />
    </template>

    <DeviceBindingDrawer
      :visible="bindingDrawerVisible"
      :object-id="bindingDrawerObjectId"
      :on-history-push="props.onHistoryPush"
      @close="bindingDrawerVisible = false"
      @open-project-connect="emit('open-project-connect')"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { exclusiveConfirm } from '@/utils/exclusiveConfirm'
import { v4 as uuidv4 } from 'uuid'
import { X, Circle, Lock, Unlock } from 'lucide-vue-next'
import { storeToRefs } from 'pinia'
import { useSceneStore, useSelectionStore } from '../stores/scene'
import { getThreeObject } from '../three/ObjectManager'
import type { LabelConfig, PhysicsBinding } from '../types/scene'
import { mergeTopLabelConfig } from '../utils/objectTopLabel'
import { connectDataSummaryForObject } from '@/connect-data/applyConnectDataWizard'
import DeviceBindingDrawer from '@/connect-data/DeviceBindingDrawer.vue'
import { registry } from '../components-lib/registry'
import { getParamSection, isParamVisible } from '../utils/paramSection'
import { buildObjectDisplayNameMap } from '../utils/objectDisplayNames'
import { fullRebuild } from '../three/ObjectManager'
import SizePanel from './SizePanel.vue'
import MaterialEditPanel from './MaterialEditPanel.vue'
import ModelPartsPanel from './ModelPartsPanel.vue'

const props = defineProps<{
  objectId: string | null
  objectIds?: string[]
  /** 选中场景路径（与 objectId 互斥） */
  selectedPathId?: string | null
  onHistoryPush?: () => void
}>()

const emit = defineEmits<{ 'open-connect-data': []; 'open-project-connect': [] }>()

const sceneStore = useSceneStore()
const selectionStore = useSelectionStore()
const { primaryDetailId, bubblePos } = storeToRefs(selectionStore)

const currentPath = computed(() =>
  props.selectedPathId ? sceneStore.activePaths().find(p => p.id === props.selectedPathId) : undefined
)
const pathRefCount = computed(() => {
  if (!props.selectedPathId) return 0
  return sceneStore.activeObjects().filter(o => o.pathId === props.selectedPathId).length
})
const pathSpeedLimitDisplay = computed(() => {
  const sl = currentPath.value?.speedLimit
  return sl == null ? '' : String(sl)
})
const pathNameDraft = ref('')
watch(
  () => currentPath.value?.name,
  (n) => {
    if (n != null) pathNameDraft.value = n
  },
  { immediate: true }
)

function commitPathName() {
  if (!props.selectedPathId) return
  props.onHistoryPush?.()
  sceneStore.updatePath(props.selectedPathId, { name: pathNameDraft.value.trim() || '路径' })
}

function onPathColorInput(e: Event) {
  if (!props.selectedPathId) return
  props.onHistoryPush?.()
  sceneStore.updatePath(props.selectedPathId, { color: (e.target as HTMLInputElement).value })
}

function onPathToggle(key: 'closed' | 'smooth' | 'visible', v: boolean) {
  if (!props.selectedPathId) return
  props.onHistoryPush?.()
  sceneStore.updatePath(props.selectedPathId, { [key]: v })
}

function onPathSpeedLimitChange(raw: string) {
  if (!props.selectedPathId) return
  props.onHistoryPush?.()
  const t = raw.trim()
  if (t === '') {
    sceneStore.updatePath(props.selectedPathId, { speedLimit: null })
    return
  }
  const n = Number(t)
  if (!Number.isNaN(n)) sceneStore.updatePath(props.selectedPathId, { speedLimit: Math.max(0, n) })
}

async function deletePathFromPanel() {
  const pid = props.selectedPathId
  if (!pid) return
  const n = pathRefCount.value
  const msg = n > 0 ? `有 ${n} 辆车将失去路径分配，确定删除该路径？` : '确定删除该路径？'
  const ok = await exclusiveConfirm(msg, '删除确认')
  if (!ok) return
  props.onHistoryPush?.()
  sceneStore.removePath(pid)
  selectionStore.selectPath(null)
}

// ---- state ----
const collapsed = ref(false)
const activeTab = ref<'transform' | 'appearance' | 'behavior' | 'data'>('transform')
const appearanceTab = ref<'material' | 'parts'>('material')
const dataTab = ref<'label' | 'physicsBindings'>('label')

// ---- computed ----
/** 未选中对象/路径：仅占一条标题高度，避免占满右栏空白 */
const isIdleSelection = computed(() => !props.objectId && !props.selectedPathId)

const isMultiSelect = computed(() => (props.objectIds?.length ?? 0) > 1)

/** 属性表单作用的对象 id（多选时为 primaryDetailId） */
const effectiveObjectId = computed(() => {
  if ((props.objectIds?.length ?? 0) > 1) {
    return primaryDetailId.value ?? props.objectId
  }
  return props.objectId
})

const obj = computed(() => {
  const id = effectiveObjectId.value
  return id ? sceneStore.activeObjects().find(o => o.id === id) : undefined
})
const objName = computed(() => obj.value?.name ?? '')

const connectSummary = computed(() => {
  if (!obj.value) return { paired: false, cardFields: [] as string[] }
  return connectDataSummaryForObject(obj.value)
})

/** 已绑定服务名（v2：对象 dataServiceId → 场景 dataServices 名） */
const connectServiceName = computed(() => {
  const o = obj.value
  if (!o) return ''
  const svcs = sceneStore.getDataServices()
  const sid = o.dataServiceId ?? svcs[0]?.id ?? ''
  return svcs.find((s) => s.id === sid)?.name ?? ''
})

const connectSummaryLabel = computed(() => {
  const code = connectSummary.value.assetCode
  if (!code) return '—'
  const name = obj.value?.name?.trim()
  const base = name ? `${name}（${code}）` : code
  return connectServiceName.value ? `${connectServiceName.value} → ${base}` : base
})

/** 属性「数据」Tab 轻量绑定抽屉 */
const bindingDrawerVisible = ref(false)
const bindingDrawerObjectId = ref('')
function openBindingDrawer() {
  const id = effectiveObjectId.value
  if (!id) return
  bindingDrawerObjectId.value = id
  bindingDrawerVisible.value = true
}
const objDisplayTitle = computed(() => {
  const o = obj.value
  const id = effectiveObjectId.value
  if (!o || !id) return ''
  const map = buildObjectDisplayNameMap(sceneStore.activeObjects())
  return map.get(id) ?? objName.value ?? ''
})

/** 组件库中文名 + 内部 type id，减轻英文类型认知负担 */
const componentTypeSubtitle = computed(() => {
  const o = obj.value
  if (!o || isMultiSelect.value) return ''
  const def = registry.get(o.type)
  const cn = def?.name?.trim()
  if (!cn) return ''
  return o.type === cn ? cn : `${cn} · ${o.type}`
})

const detailLocked = computed(() => obj.value?.locked ?? false)

const objectPhysicsBindings = computed(() => {
  const id = effectiveObjectId.value
  const bindings = sceneStore.activeScene()?.physicsConfig?.bindings
  if (!id || !bindings?.length) return []
  return bindings.filter((b) => b.sourceObjectId === id)
})

function addPhysicsBinding() {
  const id = effectiveObjectId.value
  if (!id) return
  props.onHistoryPush?.()
  sceneStore.ensurePhysicsConfig()
  const scene = sceneStore.activeScene()
  const pc = scene?.physicsConfig
  if (!pc) return
  pc.bindings.push({
    id: uuidv4(),
    sourceObjectId: id,
    sourceMetric: 'temp',
    targetType: 'zone',
    targetId: '',
    targetMetric: 'tempC',
  })
}

function removePhysicsBinding(bindingId: string) {
  props.onHistoryPush?.()
  const scene = sceneStore.activeScene()
  const pc = scene?.physicsConfig
  if (!pc) return
  pc.bindings = pc.bindings.filter((b) => b.id !== bindingId)
}

function patchPhysicsBinding(bindingId: string, patch: Partial<PhysicsBinding>) {
  props.onHistoryPush?.()
  const scene = sceneStore.activeScene()
  const pc = scene?.physicsConfig
  if (!pc) return
  const idx = pc.bindings.findIndex((b) => b.id === bindingId)
  if (idx < 0) return
  pc.bindings[idx] = { ...pc.bindings[idx]!, ...patch }
}

function chipDisplayName(id: string) {
  const map = buildObjectDisplayNameMap(sceneStore.activeObjects())
  return map.get(id) ?? id.slice(0, 8)
}

function selectedTypeName(id: string) {
  const o = sceneStore.activeObjects().find((x) => x.id === id)
  if (!o) return ''
  return registry.get(o.type)?.name?.trim() || o.type
}

function pickSelectedObject(id: string) {
  const pos = bubblePos.value ?? { x: 240, y: 120 }
  selectionStore.select(id, pos)
}

const isImportedModel = computed(() =>
  obj.value?.type === 'imported_model' || obj.value?.type === 'dxf_reference'
)

const positionDisplay = computed(() => {
  const p = obj.value?.position
  if (!p || !Array.isArray(p)) return null
  return { x: p[0].toFixed(2), y: p[1].toFixed(2), z: p[2].toFixed(2) }
})
const coordX = computed(() => obj.value?.position?.[0] ?? 0)
const coordY = computed(() => obj.value?.position?.[1] ?? 0)
const coordZ = computed(() => obj.value?.position?.[2] ?? 0)
const allPaths = computed(() => sceneStore.activePaths())
const isVehicleObject = computed(() => {
  const t = obj.value?.type ?? ''
  return t.includes('forklift') || t.includes('vehicle')
})
const vehiclePathId = computed(() => obj.value?.pathId ?? '')
const vehiclePathOffset = computed(() => Number(obj.value?.pathOffset ?? 0).toFixed(2))
const vehicleDirection = computed(() => obj.value?.direction === 'backward' ? 'backward' : 'forward')
const vehicleLoop = computed(() => obj.value?.loop === true)
const vehiclePathUsageCount = computed(() => {
  const pid = obj.value?.pathId
  if (!pid) return 0
  return sceneStore.activeObjects().filter(o => o.pathId === pid).length
})

const topLabelVisible = computed(() => obj.value?.label?.visible === true)
const statusLightVisible = computed(() => obj.value?.statusLight?.enabled === true)
const infoCardVisible = computed(() => obj.value?.infoCard?.enabled === true)
/** 信息卡真正渲染的字段（以对象 infoCard.fields 为真源，绑定字段作兜底） */
const infoCardFieldKeys = computed(() => {
  const f = obj.value?.infoCard?.fields
  if (f?.length) return f.map((x) => x.key).filter(Boolean)
  return connectSummary.value.cardFields
})
const resolvedTopLabel = computed(() => mergeTopLabelConfig(obj.value?.label))
const topLabelTextDraft = computed(() => obj.value?.label?.text ?? '')

function commitLabelPatch(patch: Partial<LabelConfig>) {
  const id = effectiveObjectId.value
  if (!id || !obj.value) return
  props.onHistoryPush?.()
  const cur = obj.value.label ?? { text: '', visible: false }
  sceneStore.updateObject(id, { label: { ...cur, ...patch } })
}

function onTopLabelVisible(v: boolean) {
  commitLabelPatch({ visible: v })
}

function onStatusLightVisible(v: boolean) {
  const id = effectiveObjectId.value
  if (!id || !obj.value) return
  props.onHistoryPush?.()
  sceneStore.updateObject(id, {
    statusLight: {
      enabled: v,
      color: statusLightColor.value,
      label: statusLightText.value,
    },
  })
}

function onInfoCardVisible(v: boolean) {
  const id = effectiveObjectId.value
  if (!id || !obj.value) return
  props.onHistoryPush?.()
  const cur = obj.value.infoCard ?? { enabled: false, fields: [] }
  sceneStore.updateObject(id, { infoCard: { ...cur, enabled: v } })
}

function onTopLabelTextCommit(raw: string) {
  commitLabelPatch({ text: raw.trim() })
}

// ---- param categorization ----
const allParamDefs = computed(() => {
  const o = obj.value
  if (!o) return []
  return (registry.get(o.type)?.params ?? []).filter(p => isParamVisible(p, o.params))
})

const colorParams = computed(() =>
  allParamDefs.value.filter(p => getParamSection(p) === 'appearance' && p.type === 'color')
)
const appearanceNonColorParams = computed(() =>
  allParamDefs.value.filter(p => getParamSection(p) === 'appearance' && p.type !== 'color')
)
const behaviorParams = computed(() =>
  allParamDefs.value.filter(p => getParamSection(p) === 'behavior')
)

/** 组件声明 mergeBehaviorToAppearance 时：behavior 参数并入「外观」tab，不再显示独立「参数」tab */
const mergeBehavior = computed(() => {
  const o = obj.value
  if (!o) return false
  return registry.get(o.type)?.mergeBehaviorToAppearance === true
})

const showAppearanceTab = computed(() =>
  colorParams.value.length > 0 ||
  appearanceNonColorParams.value.length > 0 ||
  (mergeBehavior.value && behaviorParams.value.length > 0) ||
  isImportedModel.value ||
  hasMaterialParam.value
)

/** URL/图片类自由文本参数：属性面板附加「打开文件」按钮，避免手填超长文本 */
function isUrlLikeParam(p: { key: string; label: string; type: string; options?: any[] }): boolean {
  if (p.type !== 'select' || (p.options && p.options.length > 0)) return false
  const key = p.key.toLowerCase()
  const label = p.label
  return key.includes('url') || key.includes('image') || label.includes('URL') || label.includes('图片')
}

const urlFileInputRef = ref<HTMLInputElement | null>(null)
let urlParamKey = ''

function openFileForParam(p: { key: string }) {
  urlParamKey = p.key
  urlFileInputRef.value?.click()
}

function onUrlFileChosen(e: Event) {
  const input = e.target as HTMLInputElement
  const file = input.files?.[0]
  input.value = ''
  if (!file || !urlParamKey) return
  const reader = new FileReader()
  reader.onload = () => {
    const url = String(reader.result ?? '')
    onParamChange(urlParamKey, url)
    urlParamKey = ''
  }
  reader.onerror = () => { urlParamKey = '' }
  reader.readAsDataURL(file)
}

// ---- appearance ----
const materialTypes = [
  { value: 'matte', label: '哑光' },
  { value: 'corrugated', label: '彩钢' },
  { value: 'concrete', label: '混凝土' },
  { value: 'metal', label: '金属' },
]
const hasMaterialParam = computed(() => obj.value?.params?.wallMaterial !== undefined || allParamDefs.value.some(p => p.key === 'wallMaterial'))
const currentColor = computed(() => obj.value?.params?.wallColor ?? obj.value?.params?.color ?? '#cccccc')
const currentMaterial = computed(() => obj.value?.params?.wallMaterial ?? 'matte')
const numberParamDrafts = ref<Record<string, string>>({})

function getNumberParamInputValue(key: string, current: unknown, fallback: unknown): string {
  const draft = numberParamDrafts.value[key]
  if (draft !== undefined) return draft
  const raw = current ?? fallback ?? 0
  return String(raw)
}

function onNumberParamInput(key: string, value: string) {
  numberParamDrafts.value[key] = value
}

function commitNumberParamChange(key: string, raw: string, fallback: unknown) {
  if (!effectiveObjectId.value) return
  const text = raw.trim()
  if (text === '') {
    const prev = obj.value?.params?.[key] ?? fallback ?? 0
    numberParamDrafts.value[key] = String(prev)
    return
  }
  const num = Number(text)
  if (Number.isNaN(num)) {
    const prev = obj.value?.params?.[key] ?? fallback ?? 0
    numberParamDrafts.value[key] = String(prev)
    return
  }
  delete numberParamDrafts.value[key]
  onParamChange(key, num)
}

function onColorChange(e: Event) {
  const id = effectiveObjectId.value
  if (!id) return
  props.onHistoryPush?.()
  const color = (e.target as HTMLInputElement).value
  const params: Record<string, any> = {}
  if (obj.value?.params?.wallColor !== undefined) params.wallColor = color
  else params.color = color
  sceneStore.updateObjectParams(id, params)
  const updated = sceneStore.activeObjects().find(o => o.id === id)
  if (updated) fullRebuild(updated)
}

function onParamColorChange(key: string, color: string) {
  const id = effectiveObjectId.value
  if (!id) return
  props.onHistoryPush?.()
  sceneStore.updateObjectParams(id, { [key]: color })
  const updated = sceneStore.activeObjects().find(o => o.id === id)
  if (updated) fullRebuild(updated)
}

function onMaterialChange(mat: string) {
  const id = effectiveObjectId.value
  if (!id) return
  props.onHistoryPush?.()
  sceneStore.updateObjectParams(id, { wallMaterial: mat })
  const updated = sceneStore.activeObjects().find(o => o.id === id)
  if (updated) fullRebuild(updated)
}

function onParamChange(key: string, value: any) {
  const id = effectiveObjectId.value
  if (!id) return
  props.onHistoryPush?.()
  sceneStore.updateObjectParams(id, { [key]: value })
  const updated = sceneStore.activeObjects().find(o => o.id === id)
  if (updated) fullRebuild(updated)
}

function onCoordChange(axis: 'x' | 'y' | 'z', raw: string) {
  const o = obj.value
  const id = effectiveObjectId.value
  if (!o || !id) return
  const v = Number.parseFloat(raw)
  if (Number.isNaN(v)) return
  props.onHistoryPush?.()
  const p = o.position
  if (axis === 'x') sceneStore.updateObject(id, { position: [v, p[1], p[2]] })
  else if (axis === 'y') sceneStore.updateObject(id, { position: [p[0], v, p[2]] })
  else sceneStore.updateObject(id, { position: [p[0], p[1], v] })
}

function snapToFloor() {
  const o = obj.value
  const id = effectiveObjectId.value
  if (!o || !id) return
  props.onHistoryPush?.()
  const floorId = o.floorId ?? 'f1'
  const floorElevation = sceneStore.activeFloors().find((f) => f.id === floorId)?.elevation ?? 0
  sceneStore.updateObject(id, { position: [o.position[0], floorElevation, o.position[2]] })
}

function onVehiclePathChange(pathId: string) {
  const id = effectiveObjectId.value
  if (!id) return
  props.onHistoryPush?.()
  const g = getThreeObject(id)
  const patch: Record<string, unknown> = { pathId: pathId || null }
  if (!pathId && g) {
    patch.position = [g.position.x, g.position.y, g.position.z]
    g.userData.followPathDrive = false
  }
  sceneStore.updateObject(id, patch)
}

function onVehiclePathOffsetChange(raw: string) {
  const id = effectiveObjectId.value
  if (!id) return
  const val = Number(raw)
  if (Number.isNaN(val)) return
  props.onHistoryPush?.()
  sceneStore.updateObject(id, { pathOffset: Math.max(0, Math.min(1, val)) })
}

function onVehicleDirectionChange(direction: string) {
  const id = effectiveObjectId.value
  if (!id) return
  props.onHistoryPush?.()
  sceneStore.updateObject(id, { direction: direction === 'backward' ? 'backward' : 'forward' })
}

function onVehicleLoopChange(loop: boolean) {
  const id = effectiveObjectId.value
  if (!id) return
  props.onHistoryPush?.()
  sceneStore.updateObject(id, { loop })
}

function toggleDetailLock() {
  const id = effectiveObjectId.value
  if (!id || !obj.value) return
  props.onHistoryPush?.()
  sceneStore.updateObject(id, { locked: !detailLocked.value })
}

// ---- 保存反馈 toast ----
const saveToast = ref<{ msg: string; type: 'success' | 'error' } | null>(null)
let _toastTimer: ReturnType<typeof setTimeout> | null = null
function showSaveToast(msg: string, type: 'success' | 'error' = 'success') {
  if (_toastTimer) clearTimeout(_toastTimer)
  saveToast.value = { msg, type }
  _toastTimer = setTimeout(() => { saveToast.value = null }, 2200)
}

// ---- 数据 Tab ----
const statusLightColor = ref<'green' | 'yellow' | 'red' | 'gray'>('gray')
const statusLightText = ref('未知')

const statusColors: { value: 'green' | 'yellow' | 'red' | 'gray'; label: string; color: string }[] = [
  { value: 'green', label: '绿', color: '#22c55e' },
  { value: 'yellow', label: '黄', color: '#eab308' },
  { value: 'red', label: '红', color: '#ef4444' },
  { value: 'gray', label: '灰', color: '#8899aa' },
]

function loadDataPanel() {
  const o = obj.value
  if (!o) return
  statusLightColor.value = (o.statusLight?.color ?? 'gray') as 'green' | 'yellow' | 'red' | 'gray'
  statusLightText.value = o.statusLight?.label ?? '未知'
}

watch(activeTab, (t) => {
  if (t === 'data') loadDataPanel()
})

watch(
  () => effectiveObjectId.value,
  () => {
    activeTab.value = 'transform'
    numberParamDrafts.value = {}
  }
)

function saveStatusLight() {
  const id = effectiveObjectId.value
  if (!id || !obj.value) return
  props.onHistoryPush?.()
  const enabled = obj.value.statusLight?.enabled === true
  sceneStore.updateObject(id, {
    statusLight: {
      enabled,
      color: statusLightColor.value,
      label: statusLightText.value,
    },
  })
  showSaveToast('状态标签已保存')
}
</script>

<style scoped>
.properties-panel {
  position: relative;
  width: 300px;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: rgba(14, 24, 40, 0.95);
  border: 1px solid rgba(100, 160, 240, 0.25);
  border-radius: 0;
  border-right: none;
  border-top: none;
  border-bottom: none;
  backdrop-filter: blur(8px);
  overflow: hidden;
  pointer-events: all;
}

.properties-panel.collapsed {
  flex: 0 0 40px;
}

.properties-panel--idle:not(.collapsed) {
  flex: 0 0 auto;
  min-height: 0;
}

.pp-header {
  display: flex;
  align-items: center;
  padding: 8px 10px;
  border-bottom: 1px solid rgba(100,160,240,0.15);
  flex-shrink: 0;
  gap: 6px;
  min-height: 40px;
  box-sizing: border-box;
  user-select: none;
}

.pp-title {
  flex: 1;
  font-size: 13px;
  font-weight: 600;
  color: #e0f0ff;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}

.pp-title-inner {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  width: 100%;
}

.pp-title-main {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pp-type-sub {
  font-size: 10px;
  font-weight: 500;
  color: #7a8fa8;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pp-placeholder {
  color: #4a5a70;
  font-weight: normal;
  font-style: italic;
}

.pp-collapse-btn {
  background: transparent;
  border: none;
  color: #5a6a80;
  cursor: pointer;
  font-size: 10px;
  padding: 2px 4px;
  flex-shrink: 0;
}
.pp-collapse-btn:hover { color: #7a9acc; }

/* Tabs */
.pp-tabs {
  display: flex;
  justify-content: flex-start;
  gap: 2px;
  padding: 6px 8px 0;
  flex-shrink: 0;
  position: sticky;
  top: 0;
  z-index: 1;
  background: rgba(14, 24, 40, 0.97);
}

.pp-tab {
  flex: 0 0 auto;
  padding: 4px 10px;
  background: #1a2535;
  border: 1px solid #2a3a50;
  border-radius: 5px 5px 0 0;
  color: #6b7c9e;
  cursor: pointer;
  font-size: 11px;
  transition: all 0.15s;
}

.pp-tab.active {
  background: #243550;
  border-color: #4a7aaf;
  color: #c0d8f0;
}

.pp-tab:hover:not(.active) {
  background: #1e3045;
  color: #8899aa;
}

/* 标题栏以下统一滚动，避免多选芯片把详情区挤没 */
.pp-scroll {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: contain;
}

.pp-scroll::-webkit-scrollbar { width: 6px; }
.pp-scroll::-webkit-scrollbar-track { background: transparent; }
.pp-scroll::-webkit-scrollbar-thumb { background: #2a3a50; border-radius: 3px; }

.pp-editor {
  display: flex;
  flex-direction: column;
  min-height: 0;
}

/* Body */
.pp-body {
  padding: 8px 10px;
}

/* Section */
.pp-section-label {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #5a6a80;
  margin: 6px 0 4px;
}

.pp-section-hint {
  font-size: 11px;
  color: #8899aa;
  margin-bottom: 6px;
}

.pp-row {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
  margin-bottom: 6px;
}

/* Buttons */
.pp-btn {
  flex: 1;
  padding: 5px 6px;
  background: #243040;
  border: 1px solid #3a4a60;
  border-radius: 5px;
  color: #aac;
  cursor: pointer;
  font-size: 11px;
  transition: all 0.15s;
}
.pp-btn:hover { background: #2e4060; border-color: #4a9eff; }
.pp-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.pp-btn:disabled:hover { background: #243040; border-color: #3a4a60; }

.pp-btn-danger {
  color: #fecaca;
  border-color: #7f1d1d;
  background: #3f1518;
}
.pp-btn-danger:hover {
  border-color: #ef4444;
  background: #4a1518;
}

/* Coord row */
.pp-coord-row {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
  flex-wrap: wrap;
}

.coord-label { color: #5a6a80; font-size: 11px; }

.coord-input {
  width: 60px;
  padding: 3px 6px;
  background: #1e2e42;
  border: 1px solid #3a4a60;
  border-radius: 4px;
  color: #e0f0ff;
  font-size: 12px;
}

.coord-y-hint {
  margin-left: auto;
  font-size: 11px;
  color: #4a5a70;
}

.pp-follow-floor-row {
  margin: -2px 0 8px;
}

.pp-follow-floor-label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: #8ba0bc;
  user-select: none;
}

/* 标签 tab：开关行 + 一体化设置表单 */
.pp-label-toggle-row {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 10px;
}

.pp-label-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #c0d8f0;
  user-select: none;
  cursor: pointer;
}

.pp-label-divider {
  height: 1px;
  background: #2a3a50;
  margin: 10px 0;
}

/* Mini tabs */
.pp-mini-tabs {
  display: flex;
  gap: 4px;
  margin-bottom: 8px;
}

.pp-mini-tab {
  flex: 1;
  padding: 4px 6px;
  background: #243040;
  border: 1px solid #3a4a60;
  border-radius: 5px;
  color: #8899aa;
  cursor: pointer;
  font-size: 11px;
}

.pp-mini-tab.active {
  background: #1e4a7a;
  border-color: #4a9eff;
  color: #e0f0ff;
}

/* Appearance */
.pp-appearance-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.pp-appearance-row label {
  font-size: 11px;
  color: #8899aa;
  min-width: 30px;
  flex-shrink: 0;
}

.pp-appearance-row input[type="color"] {
  width: 40px;
  height: 24px;
  border: 1px solid #3a4a60;
  border-radius: 3px;
  padding: 1px;
  background: transparent;
  cursor: pointer;
}

/* 外观 tab 网格排版：两列卡片，参数/外观合并后不再一列竖排 */
.pp-appearance-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px 10px;
  margin-bottom: 4px;
}

.pp-param-cell {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  padding: 6px 8px;
  background: rgba(20, 32, 48, 0.5);
  border: 1px solid rgba(80, 120, 180, 0.2);
  border-radius: 6px;
}

.pp-param-cell--wide {
  grid-column: 1 / -1;
}

.pp-param-cell-label {
  font-size: 11px;
  color: #7a8fa8;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.pp-param-cell-control {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.pp-param-cell-control--toggle {
  justify-content: space-between;
  gap: 8px;
}

.pp-param-cell-control input[type="checkbox"] {
  width: 15px;
  height: 15px;
  flex-shrink: 0;
}

.pp-toggle-text {
  font-size: 11px;
  color: #c0d8f0;
  line-height: 1.3;
  min-width: 0;
}

.pp-color-hex {
  font-size: 10px;
  color: #5a6a80;
  font-family: ui-monospace, monospace;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}

.pp-param-cell-control input[type="color"] {
  width: 44px;
  height: 24px;
  flex-shrink: 0;
  border: 1px solid #3a4a60;
  border-radius: 3px;
  padding: 1px;
  background: transparent;
  cursor: pointer;
}

.pp-number-input {
  width: 100%;
  flex: 1;
  min-width: 0;
  box-sizing: border-box;
}

.pp-url-row {
  display: flex;
  gap: 6px;
  width: 100%;
  min-width: 0;
  align-items: center;
}

.pp-url-row .pp-text-input {
  flex: 1;
  min-width: 0;
}

.pp-file-btn {
  flex-shrink: 0;
  padding: 3px 8px;
  background: #243040;
  border: 1px solid #3a4a60;
  border-radius: 4px;
  color: #aac;
  cursor: pointer;
  font-size: 11px;
  transition: all 0.15s;
}
.pp-file-btn:hover { background: #2e4060; border-color: #4a9eff; color: #ddf; }

.pp-material-row {
  margin-top: 8px;
}

.pp-hidden-file {
  display: none;
}

.pp-material-btns {
  display: flex;
  gap: 3px;
  flex-wrap: wrap;
}

.pp-mat-btn {
  padding: 3px 6px;
  background: #243040;
  border: 1px solid #3a4a60;
  border-radius: 4px;
  color: #8899aa;
  font-size: 10px;
  cursor: pointer;
  transition: all 0.15s;
}

.pp-mat-btn.active, .pp-mat-btn:hover {
  background: #1e4a8a;
  border-color: #4a9eff;
  color: #ddf;
}

/* Behavior params */
.pp-param-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.pp-param-label {
  font-size: 11px;
  color: #8899aa;
  min-width: 80px;
  flex-shrink: 0;
}

.pp-select {
  flex: 1;
  padding: 3px 6px;
  background: #1e2e42;
  border: 1px solid #3a4a60;
  border-radius: 4px;
  color: #c0d8f0;
  font-size: 11px;
}

/* Data panel */
.pp-data-panel {}
.pp-connect-summary {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 12px;
  padding: 10px 12px;
  border-radius: 8px;
  background: rgba(20, 32, 48, 0.55);
  border: 1px solid rgba(80, 120, 180, 0.25);
}
.pp-summary-row {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.pp-summary-label {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #5a6a80;
}
.pp-summary-value {
  font-size: 12px;
  color: #c0d8f0;
  line-height: 1.45;
}
.pp-connect-change {
  align-self: flex-start;
  margin-top: 4px;
}

.pp-card-row {
  display: flex;
  gap: 4px;
  margin-bottom: 4px;
  align-items: center;
}

.pp-card-input {
  flex: 1;
  background: #1e2e42;
  border: 1px solid #3a4a60;
  color: #c0d8f0;
  border-radius: 4px;
  padding: 3px 6px;
  font-size: 11px;
  outline: none;
  min-width: 0;
}
.pp-card-input:focus { border-color: #4a9eff; }

.pp-input-wide { flex: 1; min-width: 0; }

.pp-remove-btn {
  background: transparent;
  border: none;
  color: #556677;
  cursor: pointer;
  padding: 0 2px;
  flex-shrink: 0;
}
.pp-remove-btn:hover { color: #ff4455; }

.pp-add-btn {
  width: 100%;
  padding: 4px;
  background: transparent;
  border: 1px dashed #3a4a60;
  border-radius: 4px;
  color: #556677;
  cursor: pointer;
  font-size: 11px;
  margin-top: 4px;
}
.pp-add-btn:hover { border-color: #4a9eff; color: #4a9eff; }

.pp-panel-actions {
  display: flex;
  justify-content: flex-end;
  gap: 6px;
  margin-top: 8px;
}

.pp-confirm-btn {
  padding: 4px 14px;
  background: #1e4a8a;
  border: 1px solid #4a9eff;
  color: #ddf;
  border-radius: 4px;
  cursor: pointer;
  font-size: 11px;
}
.pp-confirm-btn:hover { background: #2a5a9a; }

/* Status light */
.pp-status-row {
  display: flex;
  gap: 6px;
  margin-bottom: 8px;
}

.pp-status-btn {
  flex: 1;
  padding: 4px 2px;
  background: #243040;
  border: 1px solid #3a4a60;
  border-radius: 5px;
  color: #aac;
  cursor: pointer;
  font-size: 11px;
  transition: all 0.15s;
}
.pp-status-btn.active { background: #1e4a8a; border-color: #4a9eff; }

.status-dot { vertical-align: -0.2em; margin-right: 4px; display: inline-block; }

.pp-text-input {
  flex: 1;
  background: #1e2e42;
  border: 1px solid #3a4a60;
  color: #c0d8f0;
  border-radius: 4px;
  padding: 3px 6px;
  font-size: 11px;
  outline: none;
}

/* Signals */
.pp-signals-panel { font-size: 12px; }

.pp-signal-hint {
  font-size: 11px;
  color: #75889e;
  line-height: 1.45;
  margin-bottom: 10px;
}

.pp-row-tight {
  margin-bottom: 6px;
}

.pp-row-tight label {
  min-width: 112px;
  flex-shrink: 0;
}

.pp-field-label {
  max-width: 100px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pp-subhead {
  font-size: 11px;
  font-weight: 600;
  color: #8aa0b8;
  margin: 12px 0 6px;
}

.pp-color-rule-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
}

.pp-signal-select {
  padding: 3px 4px;
  background: #1e2e42;
  border: 1px solid #3a4a60;
  border-radius: 4px;
  color: #c0d8f0;
  font-size: 11px;
}

.pp-threshold { width: 64px; }

.pp-col {
  flex-direction: column;
  align-items: stretch;
}
.pp-col label { min-width: auto; margin-bottom: 4px; }

.pp-signal-textarea {
  width: 100%;
  box-sizing: border-box;
  background: #1e2e42;
  border: 1px solid #3a4a60;
  border-radius: 4px;
  color: #c0d8f0;
  font-size: 11px;
  font-family: ui-monospace, monospace;
  padding: 6px;
  resize: vertical;
  outline: none;
}
.pp-signal-textarea:focus { border-color: #4a9eff; }

/* 多选：仅列出选中对象名称 */
.pp-selection-list {
  padding: 8px 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.pp-name-row {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  width: 100%;
  box-sizing: border-box;
  padding: 6px 8px;
  background: #1a2434;
  border: 1px solid #3a4a60;
  border-radius: 4px;
  color: #c8dcf0;
  cursor: pointer;
  text-align: left;
}

.pp-name-row:hover {
  border-color: #4a9eff;
}

.pp-name-row.active {
  border-color: #4a9eff;
  background: #1e3a5c;
}

.pp-name-row-title {
  font-size: 12px;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
}

.pp-name-row-type {
  font-size: 10px;
  color: #7a8fa8;
}

.pp-color-input {
  width: 36px;
  height: 28px;
  padding: 0;
  border: none;
  cursor: pointer;
  vertical-align: middle;
}

.pp-lock-row .pp-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  justify-content: center;
}

.pp-lock-icon {
  flex-shrink: 0;
}

.pp-physics-bindings {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.pp-physics-row {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 4px 6px;
  align-items: center;
  padding: 8px;
  border-radius: 6px;
  background: rgba(20, 32, 48, 0.5);
  border: 1px solid rgba(80, 120, 180, 0.2);
}

.pp-physics-row .pp-remove-btn {
  grid-column: 1 / -1;
  justify-self: end;
}

.pp-physics-label {
  font-size: 10px;
  color: #7a93b5;
}

/* 保存反馈 toast */
.pp-save-toast {
  position: sticky;
  top: 0;
  z-index: 10;
  padding: 5px 10px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  text-align: center;
  margin-bottom: 6px;
}
.pp-save-toast--ok {
  background: rgba(16, 60, 28, 0.92);
  border: 1px solid #22c55e;
  color: #86efac;
}
.pp-save-toast--error {
  background: rgba(60, 16, 18, 0.92);
  border: 1px solid #ef4444;
  color: #fca5a5;
}
.pp-toast-fade-enter-active,
.pp-toast-fade-leave-active {
  transition: opacity 0.2s, transform 0.2s;
}
.pp-toast-fade-enter-from,
.pp-toast-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
